import { FormConfig } from '@features/dynamicForm/types';
import { useTranslation } from 'react-i18next';

import {
    BaseOrderPayload,
    OrderDeliveryItem,
    WizardAccumulatedData,
} from '../types';
import { useMemo } from 'react';
import { useDeliveryContext } from '@features/delivery/components/DeliveryModal/DeliveryContext';

export const useSharedOrderConfigs = () => {
    const { t } = useTranslation('serviceOrders');
    const { validItems, selectedIds } = useDeliveryContext<
        OrderDeliveryItem,
        BaseOrderPayload
    >();

    return useMemo(() => {
        const selectedItems = validItems.filter((item) =>
            selectedIds.includes(item.id),
        );

        const canEraseProxy =
            selectedItems.length > 0 &&
            selectedItems.every((item) => item.hasProxy);

        const allOptions = [
            { label: 'Transcode', value: 'transcode' },
            { label: 'PAD', value: 'pad' },
            { label: 'Extracts', value: 'extracts' },
            { label: 'Watermark', value: 'watermark' },
            { label: 'Proxy', value: 'proxy' },
            {
                label: 'Effacer le proxy',
                value: 'erase_proxy',
                disabled: !canEraseProxy,
            },
            { label: "Demander l'effacement", value: 'deletion' },
            { label: 'Other', value: 'other' },
        ];

        const availableOptions =
            validItems.length === 0
                ? allOptions.filter((opt) => opt.value === 'other')
                : allOptions;

        const baseConfig: FormConfig<WizardAccumulatedData> = {
            id: 'step-base',
            title: t('wizard.step1.title'),
            description: t('wizard.step1.desc'),
            fields: [
                {
                    name: 'orderType',
                    label: t('fields.orderType.label'),
                    type: 'select',
                    placeholder: t('fields.orderType.placeholder'),
                    options: availableOptions,
                    validation: {
                        required: t('validations.required'),
                        validate: (value) => {
                            if (value === 'erase_proxy' && !canEraseProxy) {
                                return t('validations.proxyRequired');
                            }
                            return true;
                        },
                    },
                },
                {
                    name: 'references',
                    label: t('fields.references.label'),
                    type: 'textarea',
                    placeholder: t('fields.references.placeholder'),
                },
            ],
        };

        const finalConfig: FormConfig<WizardAccumulatedData> = {
            id: 'step-final',
            title: t('wizard.final.title'),
            description: t('wizard.final.desc'),
            fields: [
                {
                    name: 'delay',
                    label: t('fields.delay.label'),
                    type: 'date',
                    placeholder: t('fields.delay.placeholder'),
                },
                {
                    name: 'comment',
                    label: t('fields.comment.label'),
                    type: 'textarea',
                    placeholder: t('fields.comment.placeholder'),
                },
            ],
        };

        return { baseConfig, finalConfig };
    }, [selectedIds, t, validItems]);
};
